// Client HTTP commun à tous les hooks générés par orval (ADR 0013). Seul endroit où l'on appelle `fetch`.
// Authentification par cookie de session SPA (Sanctum, ADR 0023) : `credentials: 'include'` et en-tête X-XSRF-TOKEN.
import { ApiError, type ApiErrorBody } from '@/lib/errors';

// Vide ou absente = /api (même origine ; .env.example). `||` : une variable vide au build vaut ''.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

/** Racine du serveur de l'API (sans `/api`) : `/sanctum/csrf-cookie` n'est pas versionné. */
const SERVER_BASE = API_BASE.replace(/\/api\/?$/, '');

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** En-tête `If-Match` à partir de la `version` d'une ressource (socle §4.2, ADR 0006). */
export function ifMatch(version: number): RequestInit {
  return { headers: { 'If-Match': String(version) } };
}

/** Clé d'idempotence explicite (créations hors ligne, paiements). */
export function idempotent(key: string = newIdempotencyKey()): RequestInit {
  return { headers: { 'Idempotency-Key': key } };
}

/**
 * Cookie CSRF de Sanctum, obligatoire avant la première requête d'écriture d'une session (connexion, activation…).
 * Sans effet s'il est déjà présent.
 */
export async function ensureCsrfCookie(): Promise<void> {
  if (readCookie('XSRF-TOKEN')) return;
  try {
    await fetch(`${SERVER_BASE}/sanctum/csrf-cookie`, { credentials: 'include', headers: { Accept: 'application/json' } });
  } catch (cause) {
    throw ApiError.network(cause);
  }
}

export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'XMLHttpRequest');

  const xsrf = readCookie('XSRF-TOKEN');
  if (xsrf && method !== 'GET') headers.set('X-XSRF-TOKEN', xsrf);
  // Toute création peut être rejouée sans doublon (hors ligne, double clic).
  if (method === 'POST' && !headers.has('Idempotency-Key')) headers.set('Idempotency-Key', newIdempotencyKey());

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${url}`, { ...options, headers, credentials: 'include' });
  } catch (cause) {
    throw ApiError.network(cause);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('Content-Type')?.includes('application/json') ?? false;
  const body: unknown = isJson ? await response.json() : await response.blob();

  if (!response.ok) {
    throw ApiError.fromResponse(response.status, isJson ? (body as ApiErrorBody) : undefined);
  }

  return body as T;
}

export default apiFetch;
